import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getScreenerData, type ScreenerMetric } from '@/lib/api'
import { boundHistoryRange, bufferedHistoryRange, historyDomain, historyWindowCovers, initialHistoryRange,
  type ChartTimeRange } from '@/lib/screenerHistory'
import { ScreenerHistoryCache } from '@/lib/screenerHistoryCache'
import { mergeScreenerTail } from '@/lib/screenerDataMerge'
import type { ScreenerTickerData, ScreenerTimeframe } from '@/types'

type Snapshot = { compatibility: string; query: string; data: ScreenerTickerData }

/** The viewport owns navigation; live data can change without moving it. */
export function useScreenerHistory(symbol: string, liveData: ScreenerTickerData | null,
  interval: string, timeframe: ScreenerTimeframe | undefined, exchanges: string[], includeOIOhlc = false,
  markets?: string[], metrics?: ScreenerMetric[]) {
  const exchangeKey = [...exchanges].sort().join(',')
  const marketKey = markets ? [...markets].sort().join(',') : undefined
  const metricKey = metrics ? [...metrics].sort().join(',') : undefined
  const compatibility = JSON.stringify([symbol.toUpperCase(), exchangeKey, marketKey, includeOIOhlc])
  const query = JSON.stringify([compatibility, interval, metricKey])
  const view = JSON.stringify([query, timeframe])
  const navigationKey = JSON.stringify([symbol.toUpperCase(), interval, timeframe])
  const followKey = JSON.stringify([symbol.toUpperCase(), interval])
  const [viewport, setViewport] = useState<{ view: string; range: ChartTimeRange | null } | null>(null)
  const [follow, setFollow] = useState<{ query: string; span: number; right: number } | null>(null)
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const [loading, setLoading] = useState(false)
  const [failure, setFailure] = useState<{ view: string; message: string } | null>(null)
  const [retry, setRetry] = useState(0)
  const cacheRef = useRef(new ScreenerHistoryCache())
  const invalidationsRef = useRef(new Map<string, number>())
  const repairRef = useRef<string | null>(null)
  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const receivedRef = useRef<{ data: ScreenerTickerData; query: string } | null>(null)
  // A parent render can momentarily contain the previous request's object while
  // its layout effect clears data. Never relabel that object as a new scope.
  if (liveData && receivedRef.current?.data !== liveData) receivedRef.current = { data: liveData, query }
  const currentLive = liveData && receivedRef.current?.query === query
    && liveData.symbol.toUpperCase() === symbol.toUpperCase()
    && (liveData.oi_include_ohlc ?? false) === includeOIOhlc ? liveData : null
  const retained = snapshot?.compatibility === compatibility ? snapshot.data : null
  const initial = initialHistoryRange(currentLive || retained, interval, timeframe)
  const absoluteFrom = timeframe?.mode === 'absolute' ? initial.from : undefined
  const absoluteTo = timeframe?.mode === 'absolute' ? initial.to : undefined
  const selection = useMemo(() => viewport?.view === navigationKey ? viewport.range
    : absoluteFrom !== undefined && absoluteTo !== undefined ? { from: absoluteFrom, to: absoluteTo } : null,
  [viewport, navigationKey, absoluteFrom, absoluteTo])
  const centered = follow?.query === followKey ? follow : null
  const latest = initialHistoryRange(currentLive || retained, interval, { mode: 'live' }).to
  const range = selection || (centered
    ? { from: latest - centered.span * (1 - centered.right), to: latest + centered.span * centered.right }
    : initial)
  const cached = cacheRef.current.get(query, range, interval)
  const viewStateRef = useRef({ range, selection, manuallyNavigated: viewport?.view === navigationKey })
  viewStateRef.current = { range, selection, manuallyNavigated: viewport?.view === navigationKey }
  const initialDataReady = currentLive !== null
  const commit = useCallback((data: ScreenerTickerData) => {
    const next = { compatibility, query, data }
    snapshotRef.current = next
    setSnapshot(next)
  }, [compatibility, query])

  const invalidationGeneration = currentLive?.history_invalidation?.generation
  const invalidationFrom = currentLive?.history_invalidation?.from_ts
  useEffect(() => {
    if (invalidationGeneration === undefined
      || invalidationsRef.current.get(symbol) === invalidationGeneration) return
    invalidationsRef.current.set(symbol, invalidationGeneration)
    cacheRef.current.invalidate(symbol, invalidationFrom)
    const selection = viewStateRef.current.selection
    if (selection && (invalidationFrom == null || selection.to >= invalidationFrom)) repairRef.current = query
    setRetry(value => value + 1)
  }, [invalidationGeneration, invalidationFrom, query, symbol])

  useEffect(() => {
    if (!currentLive) return
    const { range, selection, manuallyNavigated } = viewStateRef.current
    const span = range.to - range.from
    cacheRef.current.put(query, currentLive, span)
    const previous = snapshotRef.current
    if (selection && repairRef.current === query) {
      const window = currentLive.history_window
      if (window && !window.truncated && historyWindowCovers({ from: window.from_ts, to: window.to_ts }, {
        from: selection.from, to: Math.min(selection.to, currentLive.oi_history_until || window.to_ts),
      })) {
        repairRef.current = null
        commit(currentLive) // A correction can remove points; never union an authoritative replacement.
      }
      return
    }
    if (!selection || !manuallyNavigated || previous?.query !== query) {
      if (!selection) repairRef.current = null
      commit(currentLive)
      return
    }
    // Older inspected windows remain stable. Overlapping live observations and
    // corrections update the current window without changing its coordinates.
    const window = previous.data.history_window
    const firstLive = Math.min(...Object.values(currentLive.exchanges).flatMap(data =>
      [data.oi_history?.[0]?.timestamp, data.trade_price_history?.[0]?.timestamp, data.candles?.[0]?.timestamp,
        data.volume_history?.[0]?.timestamp, data.cvd_history?.[0]?.timestamp]
        .filter((timestamp): timestamp is number => timestamp !== undefined)), Infinity)
    const authority = currentLive.oi_authoritative_window
    const overlapsAuthority = authority && range.to >= authority.from_ts && range.from <= authority.to_ts
    if ((overlapsAuthority || (range.to >= firstLive && range.from <= (currentLive.oi_history_until || Date.now() / 1000)))
      && previous.data.oi_interval === currentLive.oi_interval) {
      const merged = mergeScreenerTail(previous.data, currentLive, {
        from: window?.from_ts ?? range.from, to: Math.max(window?.to_ts ?? 0, Math.min(Date.now() / 1000, range.to)), maxPoints: 5000,
      })
      cacheRef.current.put(query, merged, span)
      commit(merged)
    }
  }, [currentLive, query, commit])

  useEffect(() => {
    if (!selection) return
    const known = cacheRef.current.get(query, selection, interval)
    if (known) { repairRef.current = null; commit(known); setFailure(null); return }
    // The matching full request owns initial hydration. Navigation can also use
    // cached data while that request is still in flight.
    if (!initialDataReady) return
    if (selection.from >= Date.now() / 1000) return // Entirely empty future space.
    const controller = new AbortController()
    const requested = bufferedHistoryRange(selection)
    if (requested.to <= requested.from) return
    const timer = setTimeout(async () => {
      setLoading(true)
      setFailure(null)
      try {
        const result = await getScreenerData(symbol, { mode: 'absolute', from_ts: requested.from, to_ts: requested.to },
          interval, false, controller.signal, exchangeKey ? exchangeKey.split(',') : [], requested, includeOIOhlc,
          { includeFallback: false, markets: marketKey === undefined ? undefined : marketKey ? marketKey.split(',') : [],
            metrics: metricKey === undefined ? undefined : metricKey ? metricKey.split(',') as ScreenerMetric[] : [] })
        if (controller.signal.aborted) return
        if (!result.ok || !result.data) throw new Error(result.error || 'Unable to load chart history')
        if (result.data.history_window?.truncated) throw new Error('This range contains too many points. Choose a larger interval or a shorter range.')
        if (result.data.symbol.toUpperCase() !== symbol.toUpperCase()) throw new Error('Received history for another symbol')
        cacheRef.current.put(query, result.data, selection.to - selection.from)
        repairRef.current = null
        commit(result.data)
      } catch (cause) {
        if (!controller.signal.aborted) setFailure({ view, message: cause instanceof Error ? cause.message : 'Unable to load chart history' })
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, 180)
    return () => { clearTimeout(timer); controller.abort(); setLoading(false) }
  }, [selection, view, query, symbol, interval, exchangeKey, marketKey, metricKey,
    includeOIOhlc, retry, initialDataReady, commit])

  // CVD cannot be appended to an independently anchored cumulative history.
  // Refresh that metric alone for pinned windows touching the live edge; OI,
  // price and volume continue on the small revision-aware live path above.
  const effectiveCvdInterval = snapshot?.query === query ? snapshot.data.oi_interval : interval
  useEffect(() => {
    if (!selection || !initialDataReady || (metricKey !== undefined && !metricKey.split(',').includes('cvd'))
      || selection.to < Date.now() / 1000 - 60) return
    let stopped = false
    let controller: AbortController | null = null
    let timer: ReturnType<typeof setTimeout>
    const refreshContext = () => {
      const current = snapshotRef.current
      const window = current?.query === query ? current.data.history_window : undefined
      const buffered = window ? { from: window.from_ts, to: window.to_ts } : bufferedHistoryRange(selection)
      const requested = { from: buffered.from,
        to: Math.floor(Math.min(Date.now() / 1000, Math.max(buffered.to, selection.to))) }
      const effectiveInterval = current?.query === query ? current.data.oi_interval : interval
      const fast = effectiveInterval === '5s' && Math.floor((requested.to - requested.from) / 5) + 1 <= 500
        && Object.values(current?.data.exchanges || {}).every(data => !data.cvd_interval || data.cvd_interval === '5s')
      return { current, window, requested, effectiveInterval, fast }
    }
    const delay = () => refreshContext().fast ? 2_000 : 15_000
    const refreshCvd = async () => {
      const { current, window, requested, effectiveInterval, fast } = refreshContext()
      if (!stopped && repairRef.current !== query && current && window && window.from_ts <= selection.from
        && Object.values(current.data.exchanges).some(data => data.cvd_history !== undefined)
        && (typeof document === 'undefined' || document.visibilityState !== 'hidden')) {
        controller = new AbortController()
        try {
          const result = await getScreenerData(symbol, { mode: 'absolute', from_ts: requested.from, to_ts: requested.to },
            effectiveInterval, false, controller.signal, exchangeKey ? exchangeKey.split(',') : [], requested, includeOIOhlc,
            { includeFallback: false, limit: fast ? 500 : 5000,
              markets: marketKey === undefined ? undefined : marketKey ? marketKey.split(',') : [], metrics: ['cvd'] })
          const latestSnapshot = snapshotRef.current
          if (!stopped && !controller.signal.aborted && result.ok && result.data
            && !result.data.history_window?.truncated && latestSnapshot?.query === query) {
            const merged = mergeScreenerTail(latestSnapshot.data, result.data, { replaceCvd: true, maxPoints: 5000 })
            cacheRef.current.put(query, merged, selection.to - selection.from)
            commit(merged)
          }
        } catch { /* Keep the last complete CVD snapshot; the next bounded read retries. */ }
      }
      if (!stopped) timer = setTimeout(refreshCvd, delay())
    }
    timer = setTimeout(refreshCvd, delay())
    return () => { stopped = true; clearTimeout(timer); controller?.abort() }
  }, [selection, initialDataReady, view, query, interval, symbol, exchangeKey,
    marketKey, metricKey, includeOIOhlc, commit, effectiveCvdInterval])

  const navigate = useCallback((next: ChartTimeRange) => {
    if (!Number.isFinite(next.from) || !Number.isFinite(next.to) || next.to <= next.from) return
    next = boundHistoryRange(next, interval)
    setViewport(current => current?.view === navigationKey && current.range
      && Math.abs(current.range.from - next.from) < 0.01 && Math.abs(current.range.to - next.to) < 0.01
      ? current : { view: navigationKey, range: next })
  }, [navigationKey, interval])
  const goLive = useCallback(() => {
    repairRef.current = null
    setViewport({ view: navigationKey, range: null })
    setFollow(null)
    setFailure(null)
  }, [navigationKey])
  const centerCurrent = useCallback(() => {
    repairRef.current = null
    setViewport({ view: navigationKey, range: null })
    setFollow({ query: followKey, span: range.to - range.from, right: 0.5 })
    setFailure(null)
  }, [navigationKey, followKey, range.to, range.from])
  const displayed = selection ? cached || retained || currentLive : currentLive || cached || retained
  const domain = historyDomain(range, displayed?.history_window?.earliest_ts)
  const { from, to } = range
  const { from: domainFrom, to: domainTo } = domain
  const navigation = useMemo(() => ({ range: { from, to }, domain: { from: domainFrom, to: domainTo }, navigate }),
    [from, to, domainFrom, domainTo, navigate])
  return { data: displayed, navigation, browsing: selection !== null, centered: !selection && !!centered,
    loading: loading || repairRef.current === query || (!currentLive && !cached), error: failure?.view === view ? failure.message : null,
    goLive, centerCurrent, retry: () => setRetry(value => value + 1) }
}
